import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from "react-helmet-async";
import { useApi } from '../hooks/useApi';
import FearGreedGauge from '../components/FearGreedGauge';

const parseNewsDate = (dateInput) => {
  if (typeof dateInput === 'number') {
    return new Date(dateInput * 1000);
  }

  if (typeof dateInput === 'string' && /^\d+$/.test(dateInput)) {
    return new Date(Number(dateInput) * 1000);
  }

  return new Date(dateInput);
};

const formatArticleTime = (dateInput) => {
  const date = parseNewsDate(dateInput);
  if (Number.isNaN(date.getTime())) return "Date unavailable";

  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 60) return "Just now";

  const hours = seconds / 3600;
  if (hours >= 48) {
    return date.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }

  if (hours >= 1) return Math.floor(hours) + "h ago";

  const minutes = seconds / 60;
  if (minutes >= 1) return Math.floor(minutes) + "m ago";

  return Math.floor(seconds) + "s ago";
};

const Home = () => {
  const { data: newsData, fetchData: fetchNews } = useApi('/api/news?per_page=3');
  const { data: sentimentData } = useApi('/api/sentiment');
  const news = newsData?.data ? newsData.data.slice(0, 3) : [];
  const outlook = sentimentData?.outlook || "Market data is loading. Sentiment analysis will update once fresh snapshots are available.";
  const sentiment = {
    value: sentimentData?.fear_greed?.value ?? null,
    label: sentimentData?.fear_greed?.classification || "Loading...",
    updatedAt: sentimentData?.updated_at || null,
  };

  useEffect(() => {
    const refreshId = window.setInterval(() => {
      fetchNews('/api/news?per_page=3');
    }, 10 * 60 * 1000);

    return () => window.clearInterval(refreshId);
  }, [fetchNews]);

  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <title>Live Crypto Prices, News & Market Movers | CryptoBag</title>
        <meta name="description" content="Up-to-the-minute cryptocurrency prices, charts, and market movers. Explore top gainers, losers, and market sentiment on the CryptoBag dashboard." />
        <link rel="canonical" href="https://cryptobag.net/" />
      </Helmet>

      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8 flex-grow">
        <section className="py-12 border-b border-gray-800/50">
          <h1 className="text-5xl font-black text-white tracking-tighter mb-2 leading-tight">
            Live Crypto Prices. <span className="text-indigo-500">Market Pulse.</span>
          </h1>
          <p className="text-gray-400 text-lg max-w-3xl">
            Track major coins, market sentiment, and current crypto headlines in one dashboard.
          </p>
        </section>

        <section className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="md:col-span-2 bg-gray-900/50 border border-gray-800 p-6 rounded-xl hover:border-indigo-500/30 transition-colors">
            <h2 className="text-xs font-bold text-indigo-400 uppercase tracking-widest mb-4">Live Analysis</h2>
            <div className="space-y-4">
              <p className="text-xl font-medium text-gray-200 leading-tight italic">
                "{outlook}"
              </p>
              {sentiment.updatedAt && (
                <p className="text-xs uppercase tracking-widest text-gray-500">
                  Updated {new Date(sentiment.updatedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                </p>
              )}
              <Link to="/prices" className="inline-block text-sm font-bold text-indigo-500 hover:text-white transition-colors">
                Open Market Dashboard -&gt;
              </Link>
            </div>
          </div>

          <FearGreedGauge
            loading={!sentimentData}
            value={sentiment.value}
            classification={sentiment.label}
          />

          <div className="bg-indigo-600 hover:bg-indigo-500 p-6 rounded-xl transition-all cursor-pointer group shadow-lg shadow-indigo-500/10">
            <h2 className="text-xs font-bold text-indigo-100 uppercase tracking-widest mb-4">Portfolio</h2>
            <div className="text-2xl font-black text-white leading-tight uppercase italic">Track <br /> Your Bag.</div>
            <Link to="/portfolio" className="mt-4 block text-xs font-bold text-white uppercase tracking-widest group-hover:translate-x-1 transition-transform">Coming Soon -&gt;</Link>
          </div>

          <div className="md:col-span-4 bg-gray-900/50 border border-gray-800 p-6 rounded-xl">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xs font-bold text-indigo-400 uppercase tracking-widest">Latest Crypto News</h2>
              <Link to="/articles" className="text-xs text-gray-500 hover:text-white uppercase font-bold tracking-widest">View Full Newsroom</Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {news.map((item) => (
                <a
                  key={item.id}
                  href={item.url}
                  target="_blank"
                  rel="noreferrer"
                  className="group bg-gray-800/30 backdrop-blur-sm border border-gray-700/30 rounded-2xl overflow-hidden flex flex-col h-full transition-all duration-300 hover:bg-gray-800/50 hover:border-indigo-500/50 hover:-translate-y-2 shadow-xl"
                >
                  <div className="relative h-48 w-full overflow-hidden">
                    <img
                      src={item.thumb_2x || item.thumb || 'https://via.placeholder.com/400x200?text=Crypto+News'}
                      alt={item.title}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      onError={(e) => { e.target.src = 'https://via.placeholder.com/400x200?text=Crypto+News'; }}
                    />
                    <div className="absolute top-4 left-4">
                      <span className="bg-indigo-600/90 backdrop-blur-md text-white text-[10px] font-bold px-2 py-1 rounded uppercase tracking-wider">
                        {item.news_site}
                      </span>
                    </div>
                  </div>

                  <div className="p-6 flex flex-col flex-grow">
                    <div className="flex items-center text-xs text-gray-500 mb-3 space-x-2">
                      <span>{formatArticleTime(item.updated_at)}</span>
                      <span>|</span>
                      <span className="text-indigo-400 font-medium">Trending</span>
                    </div>
                    <h3 className="text-xl font-bold text-white mb-3 line-clamp-2 group-hover:text-indigo-400 transition-colors">
                      {item.title}
                    </h3>
                    <p className="text-gray-400 text-sm line-clamp-3 mb-6">
                      {item.description || "Read the full report for in-depth coverage."}
                    </p>
                    <div className="mt-auto flex items-center text-indigo-400 text-sm font-semibold">
                      Read Story -&gt;
                    </div>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </section>
      </div>

    </div>
  );
};

export default Home;
